import React, { useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { MASK, activeFields, validateValues } from "./mapper";

const show = (f, v) => {
  if (v == null || v === "") return "";
  if (f.type === "keyvalue")
    return Object.entries(v).map(([k, val]) => `${k} = ${f.sensitive ? MASK : val}`).join("\n");
  return f.sensitive ? MASK : String(v);
};

/**
 * Review card rendered inside the chat.
 * Step 1: review/edit mapped values  ->  Step 2: "Fill form"  ->  Step 3: "Save".
 */
const IntegrationCard = ({ draft, page, onEdit, onApply, onSave, onDiscard, busy }) => {
  const [open, setOpen] = useState(true);
  const fields = activeFields(page.fields, draft.values);
  const errors = validateValues(page.fields, draft.values, page.validate);
  const errorCount = Object.keys(errors).length;
  const done = draft.saved;

  return (
    <div className="border border-[#E9D5FF] bg-white dark:bg-gray-700 rounded-lg p-3 text-xs space-y-2">
      <div className="flex justify-between items-center">
        <p className="font-semibold text-[#673AB7] dark:text-white">
          {draft.spec.method} {draft.spec.baseUrl.replace(/^https?:\/\//, "").slice(0, 38)}
        </p>
        <button onClick={() => setOpen(!open)} className="text-[11px] text-gray-500 underline">
          {open ? "Hide" : "Show"}
        </button>
      </div>

      {open &&
        fields.map((f) => {
          const v = draft.values[f.key];
          const editable = !done && ["string", "url", "enum", "number", "json"].includes(f.type) && !f.sensitive;
          return (
            <div key={f.key}>
              <label className="block font-medium text-gray-700 dark:text-gray-200">
                {f.label}
                {f.required && <span className="text-red-500"> *</span>}
              </label>
              {editable ? (
                f.type === "enum" ? (
                  <select
                    value={v || ""}
                    onChange={(e) => onEdit(f.key, e.target.value)}
                    className="w-full px-2 py-1 border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">—</option>
                    {f.options.map((o) => <option key={o}>{o}</option>)}
                  </select>
                ) : (
                  <textarea
                    rows={f.type === "json" ? 4 : 1}
                    value={v ?? ""}
                    onChange={(e) => onEdit(f.key, e.target.value)}
                    className="w-full px-2 py-1 border rounded font-mono dark:bg-gray-800 dark:border-gray-600 dark:text-white resize-none"
                  />
                )
              ) : (
                <pre className="px-2 py-1 bg-gray-50 dark:bg-gray-800 rounded whitespace-pre-wrap break-all font-mono">
                  {show(f, v) || <span className="text-gray-400">not detected</span>}
                </pre>
              )}
              {errors[f.key] && (
                <p className="text-red-600 flex items-center gap-1 mt-0.5">
                  <AlertTriangle size={12} /> {errors[f.key]}
                </p>
              )}
            </div>
          );
        })}

      {done ? (
        <p className="flex items-center gap-1 text-green-600 font-medium">
          <CheckCircle2 size={14} /> Saved.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            onClick={onApply}
            disabled={busy}
            className="bg-[#673AB7] text-white px-3 py-1.5 rounded hover:bg-[#5E35A6] disabled:opacity-60"
          >
            {draft.applied ? "Re-fill form" : "Fill form"}
          </button>
          <button
            onClick={onSave}
            disabled={busy || !draft.applied || errorCount > 0}
            title={!draft.applied ? "Fill the form first, then review it on the page" : errorCount ? "Fix the errors first" : ""}
            className="border border-[#673AB7] text-[#673AB7] dark:text-[#C4B5FD] px-3 py-1.5 rounded disabled:opacity-40 flex items-center gap-1"
          >
            {busy && <Loader2 size={12} className="animate-spin" />} Save
          </button>
          <button onClick={onDiscard} className="text-gray-500 px-2 py-1.5 hover:underline">Discard</button>
        </div>
      )}
      {draft.applied && !done && (
        <p className="text-[11px] text-gray-500">Values are on the page — review them there, then press Save.</p>
      )}
    </div>
  );
};

export default IntegrationCard;
